import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useNavigate } from 'react-router-dom';
import { FoxLogo } from '../components/FoxLogo';
import { ArrowLeft, Shield, Mail, Lock, AlertCircle, Loader2 } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
export function AdminLogin() {
  const {
    adminLogin,
    adminUser
  } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  // If already logged in, redirect
  if (adminUser) {
    navigate('/admin');
    return null;
  }
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsLoading(true);
    const result = await adminLogin(email, password);
    if (result.error) {
      setError(result.error);
      setIsLoading(false);
    } else {
      navigate('/admin');
    }
  };
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="min-h-screen bg-slate-950 flex flex-col items-center justify-center p-4 relative overflow-hidden" data-id="element-1440">
      {/* Background */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none" data-id="element-1441">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] bg-red-600/10 rounded-full blur-[100px]" data-id="element-1442" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] bg-fox-600/10 rounded-full blur-[100px]" data-id="element-1443" />
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-[0.02]" data-id="element-1444" />
      </div>

      <Link to="/" className="absolute top-8 left-8 text-slate-400 hover:text-white flex items-center gap-2 transition-colors z-10" data-id="element-1445">
        <ArrowLeft size={20} data-id="element-1446" />
        <span data-id="element-1447">Back to Site</span>
      </Link>

      <div className="w-full max-w-md relative z-10" data-id="element-1448">
        <div className="text-center mb-8" data-id="element-1449">
          <motion.div initial={{
          scale: 0
        }} animate={{
          scale: 1
        }} transition={{
          type: 'spring',
          stiffness: 260,
          damping: 20
        }} className="inline-block mb-4" data-id="element-1450">
            <div className="relative" data-id="element-1451">
              <FoxLogo className="w-16 h-16" data-id="element-1452" />
              <div className="absolute -bottom-1 -right-1 w-7 h-7 bg-red-500 rounded-full flex items-center justify-center border-2 border-slate-950" data-id="element-1453">
                <Shield size={14} className="text-white" data-id="element-1454" />
              </div>
            </div>
          </motion.div>
          <h1 className="text-3xl font-heading font-bold text-white mb-2" data-id="element-1455">
            Backend Access
          </h1>
          <p className="text-slate-400" data-id="element-1456">Authorized personnel only</p>
        </div>

        <div className="bg-slate-900 border border-slate-800 rounded-2xl shadow-2xl overflow-hidden" data-id="element-1457">
          {/* Red accent bar */}
          <div className="h-1 bg-gradient-to-r from-red-600 via-fox-500 to-red-600" data-id="element-1458" />

          <div className="p-8" data-id="element-1459">
            <div className="flex items-center gap-2 mb-6 px-4 py-3 bg-slate-950 border border-slate-800 rounded-xl" data-id="element-1460">
              <Shield size={16} className="text-red-500" data-id="element-1461" />
              <span className="text-xs text-slate-400" data-id="element-1462">
                Admin System — Fox Line Hosting
              </span>
            </div>

            {error && <motion.div initial={{
            opacity: 0,
            y: -10
          }} animate={{
            opacity: 1,
            y: 0
          }} className="mb-6 p-4 bg-red-500/10 border border-red-500/20 rounded-xl flex items-center gap-3 text-red-400 text-sm" data-id="element-1463">
                <AlertCircle size={18} data-id="element-1464" />
                {error}
              </motion.div>}

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-1465">
              <div className="space-y-2" data-id="element-1466">
                <label className="text-sm font-medium text-slate-300" data-id="element-1467">
                  Admin Email
                </label>
                <div className="relative" data-id="element-1468">
                  <Mail className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} data-id="element-1469" />
                  <input type="email" required value={email} onChange={e => setEmail(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="admin@foxlinehosting.com" data-id="element-1470" />
                </div>
              </div>

              <div className="space-y-2" data-id="element-1471">
                <label className="text-sm font-medium text-slate-300" data-id="element-1472">
                  Password
                </label>
                <div className="relative" data-id="element-1473">
                  <Lock className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-500" size={18} data-id="element-1474" />
                  <input type="password" required value={password} onChange={e => setPassword(e.target.value)} className="w-full bg-slate-950 border border-slate-800 rounded-lg pl-12 pr-4 py-3 text-white focus:outline-none focus:border-fox-500 focus:ring-1 focus:ring-fox-500 transition-colors" placeholder="••••••••" data-id="element-1475" />
                </div>
              </div>

              <button type="submit" disabled={isLoading} className="w-full bg-red-600 hover:bg-red-500 disabled:opacity-50 text-white font-medium py-3 rounded-lg transition-all shadow-[0_0_15px_rgba(220,38,38,0.2)] hover:shadow-[0_0_25px_rgba(220,38,38,0.4)] flex items-center justify-center gap-2" data-id="element-1476">
                {isLoading ? <>
                    <Loader2 size={18} className="animate-spin" data-id="element-1477" />
                    Authenticating...
                  </> : <>
                    <Shield size={18} data-id="element-1478" />
                    Sign In to Backend
                  </>}
              </button>
            </form>

            <div className="mt-6 text-center" data-id="element-1479">
              <Link to="/login" className="text-sm text-slate-500 hover:text-slate-300 transition-colors" data-id="element-1480">
                Looking for the customer portal? →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}